import type { Asset, StencilMask, TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import {
    ABSENT,
    COLOR_FILTER_NONE,
    GREYSCALE_COLOR_FILTER_NAME,
    TALKING_HEAD_MEDIA,
    UNREGISTERED,
} from '../constants';
import type { MediaColorGradingDescription, MediaEffectsDescription } from '../types';

import { formatBounds } from './boundsFormatting';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { MONO_FILTER_TEXTURE_URL } from 'js/src/data/color-grading/color-grading-slice';
import { WATERMARK_ASSET_NAME } from 'js/src/libs/consts';
import type { CropDimensions } from 'js/src/libs/crop-utils';
import {
    createContainCropState,
    createFillCropState,
    isCropStateNearTarget,
} from 'js/src/libs/crop-utils';

export type MediaFramingPreset = 'fill' | 'fit' | 'custom';
import { roundToPrecision } from 'js/src/libs/math';
import type { RootState } from 'js/src/libs/RootState';
import type { MediaEffectValues } from 'js/src/luminary/luminary-operations/advanced-media-controls';
import {
    DEFAULT_MEDIA_EFFECTS,
    getMediaEffectValues,
} from 'js/src/luminary/luminary-operations/advanced-media-controls';

/** The look values by the names set_media_effects accepts, mapped to Luminary's property names. */
export const MEDIA_EFFECT_PROPERTIES = {
    contrast: 'contrast',
    hue: 'hue',
    saturation: 'saturation',
    lightness: 'lightness',
    blur: 'blur',
    vignette: 'vignetting',
    noise: 'noise',
} as const satisfies Record<string, keyof MediaEffectValues>;
export type MediaEffectName = keyof typeof MEDIA_EFFECT_PROPERTIES;
export type MediaFlip = 'horizontal' | 'vertical' | 'both';

/**
 * Reverse-map a media asset to its MediaRegistry id — the same id `set_media` accepts. Returns
 * ABSENT when the asset isn't a media asset (or has no source data), and UNREGISTERED when it is a
 * media asset with a uuid the registry hasn't seen yet (e.g. a template default). The two are kept
 * distinct so the agent can tell "not a media asset" apart from "a media asset I haven't looked up".
 * A slot linked to its scene group (the presenter of a talking-head scene) reports
 * TALKING_HEAD_MEDIA regardless of the registry, since it holds no media of its own.
 */
export const getMediaIdFromAsset = (asset: Asset): string => {
    if (!VideoData.isMediaAsset(asset)) {
        return ABSENT;
    }
    if (asset.linkToSceneGroup) {
        return TALKING_HEAD_MEDIA;
    }
    if (!asset.sourceData) {
        return ABSENT;
    }

    const mediaRegistry = MediaRegistry.getInstance();
    const uuid = asset.sourceData.uuid;
    const mediaId = mediaRegistry.getItemIdByUuid(uuid);

    return mediaId || UNREGISTERED;
};

/**
 * The media asset's source uuid, or undefined when the asset carries no media. Unlike
 * {@link getMediaIdFromAsset} this needs no registry lookup, so it still identifies the media on
 * scenes the MediaRegistry hasn't indexed yet — which makes it the reliable way to tell whether two
 * scenes hold the same clip.
 */
export const getMediaUuidFromAsset = (asset: Asset): string | undefined => {
    if (!VideoData.isMediaAsset(asset) || !asset.sourceData) {
        return undefined;
    }
    return asset.sourceData.uuid;
};

/**
 * The media asset's kind, read from the asset itself (Luminary's `MediaAsset.isVideo`) rather than
 * inferred from a registry lookup: `true` → video, `false` → image, `null` (no source data) →
 * unknown. Keeping this asset-driven means an unregistered video is never mis-reported as an image.
 */
export const getMediaTypeFromAsset = (asset: Asset): 'video' | 'image' | 'unknown' => {
    if (!VideoData.isMediaAsset(asset)) {
        return 'unknown';
    }
    if (asset.isVideo === true) {
        return 'video';
    }
    if (asset.isVideo === false) {
        return 'image';
    }
    return 'unknown';
};

/**
 * Watermarks are ordinary media assets distinguished only by a reserved name — Luminary exposes no
 * watermark type discriminator. Reuses the shared WATERMARK_ASSET_NAME constant (same source of
 * truth as text-contrast-utils) instead of an inline magic string.
 */
export const isWatermarkAsset = (asset: Asset): boolean =>
    VideoData.isMediaAsset(asset) && asset.name === WATERMARK_ASSET_NAME;

const getMediaViewport = (
    asset: TimeAwareMediaAsset,
): { x: number; y: number; width: number; height: number } => {
    if (asset.mask) {
        return (asset.mask as StencilMask).getMaxBounds();
    }
    const { width, height } = asset.root.referenceResolution;
    return { x: 0, y: 0, width, height };
};

const roundToTenth = (value: number): number => Math.round(value * 10) / 10;

/**
 * Media covers its frame rather than letterboxing, so a shape disagreement is paid for entirely in
 * cropped-away pixels — which is why the mismatch ratio doubles as the share of the media lost.
 */
export const getMediaFrameFit = (
    asset: TimeAwareMediaAsset,
): {
    mismatch: number;
    visibleFraction: number;
    mediaAspect: number;
    frameAspect: number;
} | null => {
    const { width: frameWidth, height: frameHeight } = getMediaViewport(asset);
    const { width: mediaWidth, height: mediaHeight } = asset.sourceData ?? {};

    if (!mediaWidth || !mediaHeight || !frameWidth || !frameHeight) {
        return null;
    }

    const mediaAspect = mediaWidth / mediaHeight;
    const frameAspect = frameWidth / frameHeight;
    const mismatch = Math.max(mediaAspect, frameAspect) / Math.min(mediaAspect, frameAspect);

    return { mismatch, visibleFraction: 1 / mismatch, mediaAspect, frameAspect };
};

export const describeAspectRatio = (aspect: number): string => {
    if (aspect >= 2.5) {
        return 'wide banner';
    }
    if (aspect > 1.2) {
        return 'landscape';
    }
    if (aspect >= 0.85) {
        return 'square';
    }
    if (aspect > 0.4) {
        return 'portrait';
    }
    return 'tall strip';
};

export const getVisibleMediaBounds = (asset: TimeAwareMediaAsset): string =>
    formatBounds(getMediaViewport(asset), asset.root.referenceResolution, roundToTenth);

export const getFullMediaResolution = (asset: TimeAwareMediaAsset): string => {
    return `${Math.round(asset.width)} ${Math.round(asset.height)}`;
};

/* Get the media translation in pixels. Since the media is always centered, these values are relative to the center of the visible area. */
export const getFullMediaTranslation = (asset: TimeAwareMediaAsset): string => {
    const { width, height } = asset.mediaTargetResolution;
    // get translation in pixels and remove sign
    let xTranslation = Math.abs(asset.xTranslation * width);
    let yTranslation = Math.abs(asset.yTranslation * height);

    // due to rounding, it's possible for our x or y translation to be out of range.
    // even though this will be absorbed by Luminary, it's confusing to the user so we want to correct it here.
    const { xLimit, yLimit } = getMediaPanLimits(asset);
    if (xTranslation > xLimit) {
        xTranslation = xLimit;
    }
    if (yTranslation > yLimit) {
        yTranslation = yLimit;
    }
    // add sign correction
    if (asset.xTranslation < 0) {
        xTranslation *= -1;
    }
    if (asset.yTranslation < 0) {
        yTranslation *= -1;
    }

    // the model always seems to be confused between left/right and up/down even though we provide it as part of the setMedia tool description
    // we're going to re-iterate it here for clarity
    const xDirection =
        asset.xTranslation === 0 ? 'center' : asset.xTranslation < 0 ? 'right' : 'left';
    const yDirection = asset.yTranslation === 0 ? 'center' : asset.yTranslation < 0 ? 'down' : 'up';

    return `${Math.round(xTranslation)}(${xDirection}) ${Math.round(yTranslation)}(${yDirection})`;
};

/**
 * How far (in pixels) the media can pan on each axis: half the size difference between the media and
 * its frame. The absolute value keeps the limit positive for media zoomed out smaller than its frame
 * (after a "fit"), which Luminary still lets move within the frame — the same bound
 * setFullMediaTranslation enforces.
 */
const getMediaPanLimits = (asset: TimeAwareMediaAsset): { xLimit: number; yLimit: number } => {
    const { width, height } = getMediaViewport(asset);
    return {
        xLimit: Math.abs((asset.width - width) / 2.0),
        yLimit: Math.abs((asset.height - height) / 2.0),
    };
};

export const setFullMediaTranslation = (
    asset: TimeAwareMediaAsset,
    pixelsInX: number,
    pixelsInY: number,
) => {
    const { width, height } = getMediaViewport(asset);
    const xLimit = (asset.width - width) / 2.0;
    const yLimit = (asset.height - height) / 2.0;

    // Valid translation range is always [-abs(limit), +abs(limit)]. getFullMediaTranslation rounds
    // a pan sitting at the limit (33.75 shows as 34), so a value copied from the video state can
    // overshoot it by that rounding: clamp those onto the limit, and reject anything further out.
    const clampToLimit = (axis: 'x' | 'y', pixels: number, limit: number): number => {
        const bound = Math.abs(limit);
        if (Math.abs(pixels) > Math.max(bound, Math.round(bound))) {
            throw new Error(
                `Requested ${axis} translation ${pixels} pixels exceeds limit of ±${bound.toFixed(2)} pixels`,
            );
        }
        return Math.min(Math.max(pixels, -bound), bound);
    };
    const x = clampToLimit('x', pixelsInX, xLimit);
    const y = clampToLimit('y', pixelsInY, yLimit);
    // Luminary reads the pan as a fraction of the media's own box, not of the region it is seen
    // in, so the pixels are converted against that box; the limit is still the visible region's.
    const box = asset.mediaTargetResolution;
    asset.xTranslation = x / box.width;
    asset.yTranslation = y / box.height;
};

export const getFullMediaScale = (asset: TimeAwareMediaAsset): string => {
    const scale = Math.round(asset.scaleX * 100); // scaleX and scaleY should always be the same
    return `${scale}%`;
};

export const getMediaBrightness = (asset: TimeAwareMediaAsset): number => {
    return Math.round(asset.brightness * 100) / 100;
};

export const getMediaPlaybackMode = (asset: TimeAwareMediaAsset): 'loop' | 'hold' =>
    asset.playbackMode === VideoData.PLAYBACK_HOLD ? 'hold' : 'loop';

/**
 * The trim window into the source clip (milliseconds) plus the source's own length, so the agent can
 * tell how much of a clip is playing and whether a short clip is looping to fill its scene. endTime
 * is null when playback runs to the end of the source, so it is reported as the source duration.
 */
export const getMediaTrimInfo = (
    asset: TimeAwareMediaAsset,
): { trimStartMs: number; trimEndMs: number | null; sourceDurationMs: number | null } => {
    const sourceDurationMs = asset.sourceDuration != null ? Math.round(asset.sourceDuration) : null;
    return {
        trimStartMs: Math.round(asset.startTime),
        trimEndMs: asset.endTime != null ? Math.round(asset.endTime) : sourceDurationMs,
        sourceDurationMs,
    };
};

/** Rotation in whole degrees (positive = clockwise); undefined when upright so the field reads as "not rotated". */
export const getMediaRotation = (asset: TimeAwareMediaAsset): number | undefined => {
    const degrees = Math.round(asset.transformation.angle);
    return degrees === 0 ? undefined : degrees;
};

/** Which axes the media is mirrored on; undefined when it isn't mirrored at all. */
export const getMediaFlip = (asset: TimeAwareMediaAsset): MediaFlip | undefined => {
    if (asset.flipHorizontally && asset.flipVertically) {
        return 'both';
    }
    if (asset.flipHorizontally) {
        return 'horizontal';
    }
    if (asset.flipVertically) {
        return 'vertical';
    }
    return undefined;
};

/** Only media that covers its frame has overflow to zoom into or pan across; contained media and stickers don't. */
export const canReframeMedia = (asset: TimeAwareMediaAsset): boolean =>
    !VideoData.isStickerAsset(asset) && asset.fillMode !== VideoData.FILL_CONTAIN;

/** The source size and the frame it fills, measured the way the editor's fit/fill presets measure them. */
export const getMediaCropDimensions = (asset: TimeAwareMediaAsset): CropDimensions | null => {
    const { width: assetWidth, height: assetHeight } = asset.sourceData ?? {};
    const { width: viewportWidth, height: viewportHeight } = asset.mediaTargetResolution;
    if (!assetWidth || !assetHeight || !viewportWidth || !viewportHeight) {
        return null;
    }
    return { assetWidth, assetHeight, viewportWidth, viewportHeight, fillMode: asset.fillMode };
};

/**
 * Which of the editor's framing presets the media currently sits at: "fill" covers the frame, "fit"
 * shows the whole media inside it, and anything else is a custom zoom/pan. Undefined when the media
 * has no room to reframe (contained media, stickers) or its size isn't known yet.
 */
export const getMediaFramingPreset = (
    asset: TimeAwareMediaAsset,
): MediaFramingPreset | undefined => {
    if (!canReframeMedia(asset)) {
        return undefined;
    }
    const dimensions = getMediaCropDimensions(asset);
    if (!dimensions) {
        return undefined;
    }
    const current = {
        zoom: asset.scaleX,
        xTranslatePercent: asset.xTranslation,
        yTranslatePercent: asset.yTranslation,
    };
    if (isCropStateNearTarget(current, createFillCropState(dimensions))) {
        return 'fill';
    }
    if (isCropStateNearTarget(current, createContainCropState(dimensions))) {
        return 'fit';
    }
    return 'custom';
};

/* Get the full media translation range in pixels. This corresponds to how much the media can move within the visible area with the current scale value. We can increase the range by increasing the scale. */
export const getFullMediaTranslationRange = (
    asset: TimeAwareMediaAsset,
): { mediaXTranslationRange: [number, number]; mediaYTranslationRange: [number, number] } => {
    const { xLimit, yLimit } = getMediaPanLimits(asset);
    // Truncate rather than round: setFullMediaTranslation enforces the exact fractional limit, so
    // a rounded-up range would advertise a value the setter rejects.
    const xRange = Math.trunc(xLimit);
    const yRange = Math.trunc(yLimit);

    // `0 - n` rather than `-n` so a zero-width range reads [0, 0], not [-0, 0].
    const mediaXTranslationRange: [number, number] = [0 - xRange, xRange];
    const mediaYTranslationRange: [number, number] = [0 - yRange, yRange];

    return { mediaXTranslationRange, mediaYTranslationRange };
};

// Serialization must never throw, and a partial store (offline evals) may lack the slice.
const getColorGradingFilterName = (
    state: RootState | undefined,
    textureUrl: string,
): string | undefined =>
    state?.colorGrading?.colorGradingFilters?.find((filter) => filter.texture_url === textureUrl)
        ?.name;

/**
 * A media asset's `effects.colorGrading` (see MediaColorGradingDescription), or undefined when it
 * follows the global filter. The filter name falls back to its texture url when the store cannot
 * resolve it. A talking-head presenter slot reads the scene group's recording, where the override
 * is kept.
 */
/** A media asset's `effects` entry (see MediaAssetDescription), or undefined when its look is untouched. */
export const getMediaEffects = (
    asset: TimeAwareMediaAsset,
    state?: RootState,
): MediaEffectsDescription | undefined => {
    const effects: MediaEffectsDescription = {};
    const colorGrading = getMediaColorGradingOverride(asset, state);
    if (colorGrading) {
        effects.colorGrading = colorGrading;
    }
    const current = getMediaEffectValues(getMediaEffectsTarget(asset));
    for (const name of Object.keys(MEDIA_EFFECT_PROPERTIES) as MediaEffectName[]) {
        const property = MEDIA_EFFECT_PROPERTIES[name];
        const value = roundToPrecision(current[property], 2);
        if (value !== DEFAULT_MEDIA_EFFECTS[property]) {
            effects[name] = value;
        }
    }
    return Object.keys(effects).length > 0 ? effects : undefined;
};

/**
 * The asset whose look the editor and set_media_effects actually adjust: a talking-head presenter
 * slot mirrors the scene group's recording, so that is where its effects and grading live.
 */
const getMediaEffectsTarget = (asset: TimeAwareMediaAsset): TimeAwareMediaAsset =>
    asset.linkToSceneGroup ? (asset.slide?.sceneGroup?.mediaAsset ?? asset) : asset;

export const getMediaColorGradingOverride = (
    asset: TimeAwareMediaAsset,
    state?: RootState,
): MediaColorGradingDescription | undefined => {
    const target = getMediaEffectsTarget(asset);
    if (target.enableGreyscale) {
        return {
            filter:
                getColorGradingFilterName(state, MONO_FILTER_TEXTURE_URL) ??
                GREYSCALE_COLOR_FILTER_NAME,
            intensity: 1,
        };
    }
    const override = target.gradingOverride;
    if (!override) {
        return undefined;
    }
    if (!override.source) {
        return { filter: COLOR_FILTER_NONE, intensity: 0 };
    }
    return {
        filter: getColorGradingFilterName(state, override.source) ?? override.source,
        intensity: roundToPrecision(override.intensity, 2),
    };
};
